import React, { lazy, Suspense, useState } from 'react';
import type { ExtraProps } from 'react-markdown';
import type { SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';

import { Button, Icon, Tooltip } from '@components/Shared';

const LazyPrismHighlighter = lazy(async () => {
  const [SyntaxHighlighter, dark] = await Promise.all([
    import('react-syntax-highlighter/dist/esm/prism'),
    import('react-syntax-highlighter/dist/esm/styles/prism/a11y-dark'),
  ]);

  const highlightedStyles = {
    ...dark.default,
    ':not(pre) > code[class*="language-"]': {
      ...dark.default[':not(pre) > code[class*="language-"]'],
      padding: 0,
      background: 'none',
    },
    'pre[class*="language-"]': {
      ...dark.default['pre[class*="language-"]'],
      padding: 0,
      background: 'none',
      overflow: 'initial',
    },
  };

  const Prism = SyntaxHighlighter.default;

  const Comp = (props: SyntaxHighlighterProps) => (
    <Prism {...props} style={highlightedStyles} />
  );

  return { default: Comp };
});

type Props = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
> &
  ExtraProps;

export const Code = ({ children, className, node, ...rest }: Props) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    await navigator.clipboard.writeText(String(children));
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const isSingleLine = String(children).split(/\r\n|\r|\n/).length === 1;
  const match = /language-(\w+)/.exec(className || '');

  if (isSingleLine && !match) {
    return (
      <Button theme className="group relative inline-flex" onClick={handleCopy}>
        <code
          {...rest}
          className={`${className} rounded-md bg-neural-01 px-1 font-light text-neural-05 before:content-none after:content-none`}
        >
          {children}
        </code>

        <div className="absolute right-0 top-1/2 z-10 inline-block -translate-y-1/2 rounded-md bg-neural-01 opacity-0 transition-all group-hover:opacity-100">
          <Icon
            size="xs"
            className="h-5 w-5"
            icon={copied ? faCheck : faCopy}
          />
        </div>
      </Button>
    );
  }

  const content = match ? (
    <Suspense fallback={null}>
      <LazyPrismHighlighter
        {...(rest as SyntaxHighlighterProps)}
        PreTag="div"
        language={match[1]}
        className="rounded-md text-sm"
      >
        {String(children).replace(/\n$/, '')}
      </LazyPrismHighlighter>
    </Suspense>
  ) : (
    <code {...rest} className={className}>
      {children}
    </code>
  );

  return (
    <div className="group relative -mx-5 -my-4">
      <div className="overflow-auto py-4 pl-5 pr-8">{content}</div>

      <div className="absolute right-1 top-1 z-10 inline-block">
        <Tooltip>
          <div slot="trigger">
            <Button
              theme
              className="rounded bg-black p-0.5 text-base text-white opacity-0 transition-all will-change-transform hover:bg-neural-06 group-hover:opacity-100"
              onClick={handleCopy}
            >
              <Icon icon={copied ? faCheck : faCopy} />
            </Button>
          </div>

          {copied ? <div>Copied</div> : <div>Copy</div>}
        </Tooltip>
      </div>
    </div>
  );
};
